import React, { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/modal/Modal';
import { isCenterViewTab } from '../../lib/utils';
import { formatKst, isCheckNeededRow } from '../../features/sessionReport/labels';
import { SessionReportRow } from '../../features/sessionReport/types';
import { formatReportNumber } from './sessionReportPresentation';
import {
    createSupportAttempt, findRowSupport, getSessionReportSupport, isOpenSupport, normalizeSupportMemo, postSessionReportSupport, SUPPORT_MEMO_MAX, withSupportMemo,
    rememberSupportResult, supportAttemptKey, supportListKey, supportOptimisticKey, SupportListResult, SupportPostResult, SupportRequest, SupportRequestBody,
} from '../../features/sessionReport/support';

export const SUPPORT_GUIDANCE = '버튼 하나로 라포에 알려 주세요. 따로 연락하지 않으셔도 돼요.';
type SupportView = SupportListResult | { status: 'partial'; data: { results: SupportRequest[] } };
export const getSupportGuidance = (list: SupportView | undefined) => list?.status === 'unavailable'
    ? '회원 이름을 복사해 라포 고객센터에 알려 주세요' : SUPPORT_GUIDANCE;
export const useSupportList = (centerId: string, yearMonth: string) => {
    const client = useQueryClient();
    const optimistic = useQuery<SupportRequest[]>({ queryKey: supportOptimisticKey(centerId, yearMonth), enabled: false });
    const list = useQuery({
        queryKey: supportListKey(centerId, yearMonth), queryFn: () => getSessionReportSupport(centerId, yearMonth),
        enabled: !!centerId && !!yearMonth, retry: false, staleTime: 30000, refetchOnWindowFocus: true,
        onSuccess: () => client.setQueryData(supportOptimisticKey(centerId, yearMonth), []),
    });
    let view: SupportView | undefined = list.data;
    if (list.data?.status !== 'unavailable' && optimistic.data?.length) {
        const ids = new Set(optimistic.data.map((request) => request.supportRequestId));
        view = list.data?.status === 'ok'
            ? { ...list.data, data: { ...list.data.data, results: [...optimistic.data, ...list.data.data.results.filter((request) => !ids.has(request.supportRequestId))] } }
            : { status: 'partial', data: { results: optimistic.data } };
    }
    return { ...list, view };
};
export const SupportContext = createContext<SupportView | undefined>(undefined);
export const useRowSupport = (row: SessionReportRow) => {
    const list = useContext(SupportContext);
    return list && list.status !== 'unavailable' ? findRowSupport(list.data.results, row) : undefined;
};

/** 서랍과 회원 상세가 같은 접수·계산 시점으로 다음 할 일을 안내해요. */
export const rowSupportPresentation = (row: SessionReportRow, request?: SupportRequest, generatedAt?: string) => {
    const difference = formatReportNumber(Math.abs(row.check.diff));
    const remaining = !!request && !isOpenSupport(request) && isCheckNeededRow(row);
    const mismatch = remaining && row.check.diff !== 0;
    const needsRecalculation = remaining && !!generatedAt && !!request?.closedAt
        && Date.parse(generatedAt) < Date.parse(request.closedAt);
    const guidance = request && isOpenSupport(request) ? '라포가 확인하고 있어요'
        : remaining ? `라포가 정리했지만 아직 ${mismatch ? `${difference}회가 안 맞아요` : '확인할 내용이 남아 있어요'}. 수강권을 열어 확인해 주세요`
        : request ? '라포가 확인을 마쳤어요' : SUPPORT_GUIDANCE;
    return { remaining, needsRecalculation, guidance,
        detail: request && isOpenSupport(request) ? guidance
            : needsRecalculation ? '숫자는 다시 계산하면 반영돼요'
            : remaining ? `그래도 ${mismatch ? `${difference}회가 안 맞아요` : '확인할 내용이 남아 있어요'} · 다시 알리거나, 맞다면 처리 완료를 눌러 주세요` : '',
        remainingLabel: mismatch ? `라포 정리 뒤에도 ${difference}회 차이` : '라포 정리 뒤에도 확인이 필요해요',
    };
};

export const supportBadgeLabel = (request: SupportRequest, row: SessionReportRow, remainingProblem = false) => {
    const closed = request.status === 'DONE' || request.status === 'NOT_AN_ISSUE';
    if (remainingProblem && closed && isCheckNeededRow(row)) return rowSupportPresentation(row, request).remainingLabel;
    return request.status === 'DONE' ? '라포가 정리했어요' : request.status === 'NOT_AN_ISSUE' ? '문제 아님' : '라포 확인 중';
};

export const SupportBadge = ({ row, showNote = false, remainingProblem = false, noteId, inline = false, children }: {
    row: SessionReportRow; showNote?: boolean; remainingProblem?: boolean; noteId?: string; inline?: boolean; children?: React.ReactNode;
}) => {
    const request = useRowSupport(row);
    if (!request) return null;
    const done = request.status === 'DONE';
    const notIssue = request.status === 'NOT_AN_ISSUE';
    const remaining = remainingProblem && (done || notIssue) && isCheckNeededRow(row);
    const label = supportBadgeLabel(request, row, remainingProblem);
    const note = request.adminNote?.replace(/\s+/g, ' ') ?? '';
    return <span aria-label={`${row.memberName} 라포 접수 상태`} className={inline
        ? 'mb-[10px] block break-words text-left text-[13px] leading-[22px] text-[#4E5968]'
        : 'my-[4px] block text-left text-[12px] leading-[19px]'}>
        <span title={remaining ? label : undefined} className="inline-flex max-w-full whitespace-normal break-keep rounded-[6px] px-[8px] py-[3px] font-medium" style={{
            backgroundColor: remaining ? '#FFF7ED' : done ? '#F0FDF4' : notIssue ? '#F4F5F7' : '#EFF5FF',
            color: remaining ? '#9A4B08' : done ? '#15803D' : notIssue ? '#4E5968' : '#2B5DB3',
        }}>{label}</span>
        {note && <>{inline && showNote && ' · '}<span id={noteId} className={showNote
            ? `${inline ? '' : 'mt-[4px] block '}whitespace-normal break-words text-[#4E5968]` : 'sr-only'}>{note}</span></>}
        {children}
    </span>;
};

// 상세 창의 수명과 분리해 같은 키에는 전송 Promise가 하나만 생겨요.
// QueryClient를 나누고 요청 번호도 대조해 로그아웃 전 응답이 새 세션에 섞이지 않게 해요.
type PendingSupport = { requestId: string; promise: Promise<SupportPostResult | null> };
const pendingByClient = new WeakMap<QueryClient, Map<string, PendingSupport>>();
const pendingListeners = new Set<() => void>();
const subscribePending = (listener: () => void) => { pendingListeners.add(listener); return () => { pendingListeners.delete(listener); }; };
const notifyPending = () => pendingListeners.forEach((listener) => listener());
const getPending = (client: QueryClient, key: ReturnType<typeof supportAttemptKey>) => {
    const entry = pendingByClient.get(client)?.get(JSON.stringify(key));
    return entry?.requestId === client.getQueryData<SupportRequestBody>(key)?.requestId ? entry?.promise : undefined;
};

// 빈도 제한은 행·월·상세 창의 수명과 분리하고, 로그아웃 시 조회 캐시와 함께 지워요.
const supportCooldownKey = (centerId: string) => ['sessionReportSupportCooldown', centerId] as const;
const getSupportRetryAt = (client: QueryClient, centerId: string) => client.getQueryData<number>(supportCooldownKey(centerId)) ?? 0;
const useSupportCooldown = (client: QueryClient, centerId: string) => {
    const retryAt = useSyncExternalStore(
        (listener) => client.getQueryCache().subscribe(listener),
        () => getSupportRetryAt(client, centerId),
    );
    const [now, setNow] = useState(Date.now);
    const remainingSeconds = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
    useEffect(() => {
        if (remainingSeconds === 0) return;
        // 타이머는 표시만 바꿔요. 탭이 쉬었다 돌아와도 종료 시각으로 남은 시간을 계산해요.
        // 렌더 뒤 effect 실행 전에 만료됐어도 한 번 갱신해 버튼을 다시 켜요.
        const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, Math.min(1000, retryAt - Date.now())));
        return () => window.clearTimeout(timer);
    }, [retryAt, now, remainingSeconds]);
    return remainingSeconds;
};

const sendSupport = (client: QueryClient, centerId: string, body: Omit<SupportRequestBody, 'requestId'>) => {
    const attempt = createSupportAttempt(client, centerId, body);
    const pending = getPending(client, attempt.key);
    if (pending) return pending;
    const map = pendingByClient.get(client) ?? new Map<string, PendingSupport>();
    pendingByClient.set(client, map);
    const key = JSON.stringify(attempt.key);
    const isCurrent = () => client.getQueryData<SupportRequestBody>(attempt.key)?.requestId === attempt.body.requestId;
    const controller = new AbortController();
    const unsubscribe = client.getQueryCache().subscribe(() => { if (!isCurrent()) controller.abort(); });
    const promise = (async (): Promise<SupportPostResult | null> => {
        try {
            const result = await postSessionReportSupport(centerId, attempt.body, controller.signal);
            if (!isCurrent()) return null;
            if (result.status === 'ok') {
                if (!await rememberSupportResult(client, centerId, body.yearMonth, result.data.supportRequest, isCurrent)) return null;
                client.removeQueries({ queryKey: attempt.key, exact: true });
            } else if (result.status === 'unavailable') {
                await client.cancelQueries({ queryKey: supportListKey(centerId) });
                if (!isCurrent()) return null;
                client.setQueryData<SupportListResult>(supportListKey(centerId, body.yearMonth), { status: 'unavailable' });
                client.removeQueries({ queryKey: attempt.key, exact: true });
            } else {
                if (result.retryAfterSeconds) {
                    const retryAt = Math.max(getSupportRetryAt(client, centerId), Date.now() + result.retryAfterSeconds * 1000);
                    client.getQueryCache().build(client, { queryKey: supportCooldownKey(centerId), cacheTime: Infinity }).setData(retryAt);
                }
                if (result.renewRequest) client.removeQueries({ queryKey: attempt.key, exact: true });
            }
            return result;
        } catch {
            return isCurrent() ? { status: 'error', message: '잠시 뒤 다시 해 주세요', refresh: false, renewRequest: false } : null;
        } finally {
            unsubscribe();
            if (map.get(key)?.requestId === attempt.body.requestId) map.delete(key);
            notifyPending();
        }
    })();
    map.set(key, { requestId: attempt.body.requestId, promise });
    notifyPending();
    return promise;
};

export const SupportSummary = ({ row, generatedAt, disabled, onRefresh }: {
    row: SessionReportRow; generatedAt?: string; disabled: boolean; onRefresh: () => Promise<boolean>;
}) => {
    const request = useRowSupport(row);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const running = useRef(false);
    if (!request) return null;
    const { needsRecalculation: canRecalculate, detail } = rowSupportPresentation(row, request, generatedAt);
    return <SupportBadge row={row} showNote inline>
        {detail && <> · {detail}</>}
        {canRecalculate && <> · <button type="button" disabled={disabled || busy}
            className="text-[#2B5DB3] underline disabled:opacity-50" style={{ minHeight: 0 }} onClick={async () => {
                if (running.current) return;
                running.current = true; setBusy(true); setFailed(false);
                try { setFailed(!await onRefresh()); }
                catch { setFailed(true); }
                finally { running.current = false; setBusy(false); }
            }}>{busy ? '계산 중이에요' : '다시 계산'}</button></>}
        {canRecalculate && failed && <span role="alert" className="mt-[4px] block text-[#B91C1C]">숫자를 다시 계산하지 못했어요. 잠시 뒤 다시 해 주세요.</span>}
    </SupportBadge>;
};

export const SupportAction = ({ row, centerId, yearMonth, ghostDays, problem, numbers, disabled, fallback, onRefresh, onSuccessFocus }: {
    row: SessionReportRow & { coachName: string };
    centerId: string;
    yearMonth: string;
    ghostDays: number;
    problem: string;
    numbers: string;
    disabled: boolean;
    fallback: React.ReactNode;
    onSuccessFocus: () => void;
    onRefresh: () => Promise<boolean>;
}) => {
    const client = useQueryClient();
    const list = useContext(SupportContext);
    const request = useRowSupport(row);
    const openRequest = request && isOpenSupport(request) ? request : undefined;
    const [confirm, setConfirm] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [notice, setNotice] = useState('');
    const [memo, setMemo] = useState('');
    const [sentMemo, setSentMemo] = useState('');
    const [failure, setFailure] = useState<Extract<SupportPostResult, { status: 'error' }> | null>(null);
    const running = useRef(false);
    const alive = useRef(true);
    const trigger = useRef<HTMLButtonElement>(null);
    const focusTitle = useRef(onSuccessFocus);
    focusTitle.current = onSuccessFocus;
    const body = { yearMonth, rowKey: row.rowKey, coachId: row.coachId,
        membershipIds: row.memberships.map((m) => m.membershipId), ghostDays, includeEmpty: false };
    const key = supportAttemptKey(centerId, body);
    const pending = useSyncExternalStore(subscribePending, () => getPending(client, key));
    // 응답을 못 받은 요청 번호가 남아 있으면 그때 보낸 메모를 그대로 다시 보내요. 입력칸도 그 메모로 고정해요.
    const subscribeCache = (listener: () => void) => client.getQueryCache().subscribe(listener);
    const hasStoredAttempt = useSyncExternalStore(subscribeCache, () => client.getQueryData<SupportRequestBody>(key) !== undefined);
    const storedMemo = useSyncExternalStore(subscribeCache, () => client.getQueryData<SupportRequestBody>(key)?.memo ?? '');
    const remainingSeconds = useSupportCooldown(client, centerId);
    const cooldownMessage = remainingSeconds > 0 ? `요청이 많아요. ${remainingSeconds}초 뒤에 다시 눌러 주세요` : '';
    const busy = !!pending || refreshing;
    const observed = useRef<Promise<SupportPostResult | null>>();
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    useEffect(() => {
        if (!pending || observed.current === pending) return;
        observed.current = pending;
        // 전송이 끝나도 마운트 중인 상세 창에는 결과를 알려요.
        void pending.then(async (result) => {
            if (!alive.current || !result) return;
            if (result.status === 'error') { setFailure(result); setConfirm(true); return; }
            setConfirm(false);
            focusTitle.current();
            if (result.status === 'ok') {
                setMemo('');
                setSentMemo(normalizeSupportMemo(result.data.supportRequest.memo));
                setNotice(result.data.reason === 'ALREADY_OPEN' ? '이미 라포에 알린 회원이에요' : '라포에 알렸어요');
            } else {
                try {
                    await navigator.clipboard.writeText(row.memberName);
                    if (alive.current) setNotice('이 서버에서는 아직 알리기를 쓸 수 없어요 · 이름을 복사했어요');
                } catch {
                    if (alive.current) setNotice(`이 서버에서는 아직 알리기를 쓸 수 없어요 · 이름을 직접 복사해 주세요: ${row.memberName}`);
                }
            }
        });
    }, [pending, row.memberName]);
    useEffect(() => { if (openRequest && !busy) setConfirm(false); }, [openRequest, busy]);
    const close = () => { if (!running.current && !getPending(client, key)) { setConfirm(false); trigger.current?.focus(); } };
    const readOnly = isCenterViewTab();
    const shownMemo = sentMemo || normalizeSupportMemo(openRequest?.memo);
    const memoValue = hasStoredAttempt ? storedMemo : memo;
    const send = () => {
        if (running.current || readOnly || disabled || openRequest || getPending(client, key) || getSupportRetryAt(client, centerId) > Date.now()) return;
        setFailure(null); setNotice(''); setSentMemo('');
        void sendSupport(client, centerId, withSupportMemo(body, memo));
    };
    return <div>
        <p role="status" aria-label="라포 접수 안내" className="text-[14px] leading-[22px] text-[#4E5968]">{!confirm && cooldownMessage ? <span aria-live="off">{cooldownMessage}</span> : notice}</p>
        {shownMemo && <p title={shownMemo} className="mb-[6px] truncate text-[13px] leading-[20px] text-[#6B7684]">보낸 메모: {shownMemo}</p>}
        {list?.status === 'unavailable' ? fallback : <button ref={trigger} type="button" disabled={disabled || busy || readOnly || !!openRequest || remainingSeconds > 0} onClick={() => { setFailure(null); setMemo(''); setConfirm(true); }}
            className={`min-h-[42px] rounded-[10px] px-[16px] text-[14px] font-medium ${openRequest
                ? 'cursor-default bg-[#F2F4F6] text-[#6B7684]' : 'bg-[#3D73DD] text-white disabled:opacity-50'}`}>
            {busy && !confirm ? '알리고 있어요' : openRequest ? `라포에 알렸어요 · ${formatKst(openRequest.createdAt, 'M월 D일')}` : request ? '다시 라포에 알리기' : '라포에 알리기'}
        </button>}
        {readOnly && <p className="mt-[6px] text-[14px] text-[#4E5968]">센터 열람 중에는 알릴 수 없어요.</p>}
        {confirm && <Modal isCloseButton={false} isCloseOutside={false} onClose={close}
            whiteViewWidth="min(480px, calc(100vw - 32px))" borderRadius={20} modalAlign="center">
            <div role="alertdialog" aria-modal="true" aria-labelledby="support-title" aria-describedby="support-description" className="p-[28px]">
                <h2 id="support-title" className="text-[22px] font-semibold leading-[30px] text-[#202630]">라포에 알릴까요?</h2>
                <p id="support-description" className="mt-[12px] break-keep text-[15px] leading-[25px] text-[#4E5968]">이 회원의 수강권·숫자를 라포가 바로 확인할 수 있게 보내요. 따로 연락하지 않으셔도 돼요.</p>
                <div aria-label="보낼 내용" className="mt-[20px] rounded-[12px] bg-[#F4F7FC] p-[16px] text-[14px] leading-[24px] text-[#4E5968]">
                    <p className="font-semibold text-[#202630]">{row.memberName} · {row.coachName} 강사</p>
                    <p className="mt-[6px] break-keep">{problem}</p>
                    <p className="mt-[6px] break-keep">{numbers}</p>
                </div>
                {!readOnly && <div className="mt-[16px]">
                    <div className="flex items-center justify-between">
                        <label htmlFor="support-memo" className="text-[14px] font-medium leading-[22px] text-[#202630]">라포에 남길 말 (선택)</label>
                        <span id="support-memo-count" aria-live="off" className="text-[13px] leading-[20px] text-[#8B95A1]">{memoValue.length}/{SUPPORT_MEMO_MAX}</span>
                    </div>
                    <textarea id="support-memo" value={memoValue} maxLength={SUPPORT_MEMO_MAX} rows={3}
                        readOnly={hasStoredAttempt} disabled={busy} aria-describedby="support-memo-count"
                        placeholder="예: 10월 2일 결제가 두 번 잡혔어요"
                        onChange={(event) => setMemo(event.target.value.slice(0, SUPPORT_MEMO_MAX))}
                        className="mt-[6px] block w-full resize-none rounded-[10px] border border-[#D1D6DB] bg-white px-[12px] py-[10px] text-[14px] leading-[22px] text-[#202630] placeholder:text-[#B0B8C1] focus:border-[#3D73DD] focus:outline-none read-only:bg-[#F9FAFB] disabled:bg-[#F9FAFB]" />
                    {hasStoredAttempt && !busy && <p className="mt-[4px] text-[13px] leading-[20px] text-[#6B7684]">앞서 보내던 내용 그대로 다시 보내요.</p>}
                </div>}
                {(failure || cooldownMessage) && <div role="alert" className="mt-[16px] text-[14px] leading-[23px] text-[#B91C1C]">
                    {/* 매초 바뀌는 대기 문구는 다시 읽지 않는다 */}
                    <p aria-live={cooldownMessage ? 'off' : undefined}>{cooldownMessage || failure?.message}</p>
                    {failure?.refresh && <button type="button" disabled={busy} className="mt-[4px] min-h-[40px] underline" onClick={async () => {
                        if (running.current) return;
                        running.current = true; setRefreshing(true);
                        try {
                            if (await onRefresh()) { setConfirm(false); trigger.current?.focus(); }
                            else setFailure({ ...failure, message: '숫자를 새로 불러오지 못했어요. 잠시 뒤 새로고침해 주세요.' });
                        } catch {
                            if (alive.current) setFailure({ ...failure, message: '숫자를 새로 불러오지 못했어요. 잠시 뒤 새로고침해 주세요.' });
                        }
                        finally { running.current = false; if (alive.current) setRefreshing(false); }
                    }}>새로고침</button>}
                </div>}
                <div className="mt-[24px] flex justify-end gap-[8px]">
                    <button type="button" disabled={busy || failure?.refresh || remainingSeconds > 0} onClick={() => void send()}
                        className="min-h-[44px] rounded-[10px] bg-[#3D73DD] px-[20px] text-[15px] font-medium text-white disabled:opacity-50">{busy ? '알리고 있어요' : failure ? '다시 알리기' : '알리기'}</button>
                    <button type="button" autoFocus disabled={busy} onClick={close}
                        className="min-h-[44px] rounded-[10px] bg-[#F2F4F6] px-[20px] text-[15px] text-[#4E5968] disabled:opacity-50">취소</button>
                </div>
            </div>
        </Modal>}
    </div>;
};
